import { useEffect, useRef, useState } from 'react'
import { Globe, Loader2, ShieldOff } from 'lucide-react'
import { isValidUrl } from '@/lib/buildUrl'

const EMBED_MESSAGE_SOURCE = 'n3o-link-builder-embed'
const EMBED_DETECTION_TIMEOUT_MS = 1500

interface PreviewPaneProps {
  url: string
  onIframeNavigated?: (url: string) => void
}

type Check =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'embeddable' }
  | { status: 'blocked' }
  | { status: 'unverified' }

interface IframeNavMessage {
  source: typeof EMBED_MESSAGE_SOURCE
  type: 'nav'
  url: string
  version?: number
}

function isIframeNavMessage(data: unknown): data is IframeNavMessage {
  if (data === null || typeof data !== 'object') return false
  const obj = data as Record<string, unknown>
  return (
    obj.source === EMBED_MESSAGE_SOURCE &&
    obj.type === 'nav' &&
    typeof obj.url === 'string'
  )
}

export function PreviewPane({ url, onIframeNavigated }: PreviewPaneProps) {
  const [check, setCheck] = useState<Check>({ status: 'idle' })
  const [embed, setEmbed] = useState<'unknown' | 'present' | 'missing'>('unknown')
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const embedTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isValidUrl(url)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset to idle when url becomes invalid
      setCheck({ status: 'idle' })
      return
    }

    setCheck({ status: 'checking' })
    const controller = new AbortController()

    fetch(`./api/check-embeddable?url=${encodeURIComponent(url)}`, {
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`status ${res.status}`)
        const data: unknown = await res.json()
        const embeddable =
          data !== null &&
          typeof data === 'object' &&
          (data as { embeddable?: unknown }).embeddable === true
        setCheck({ status: embeddable ? 'embeddable' : 'blocked' })
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        setCheck({ status: 'unverified' })
      })

    return () => controller.abort()
  }, [url])

  useEffect(() => {
    let expectedOrigin: string
    try {
      expectedOrigin = new URL(url).origin
    } catch {
      return
    }
    function handler(event: MessageEvent) {
      if (event.source !== iframeRef.current?.contentWindow) return
      if (event.origin !== expectedOrigin) return
      if (!isIframeNavMessage(event.data)) return
      setEmbed('present')
      if (embedTimerRef.current !== null) {
        window.clearTimeout(embedTimerRef.current)
        embedTimerRef.current = null
      }
      onIframeNavigated?.(event.data.url)
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [onIframeNavigated, url])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset on url change
    setEmbed('unknown')
    return () => {
      if (embedTimerRef.current !== null) {
        window.clearTimeout(embedTimerRef.current)
        embedTimerRef.current = null
      }
    }
  }, [url])

  function handleIframeLoad() {
    if (embedTimerRef.current !== null) {
      window.clearTimeout(embedTimerRef.current)
    }
    embedTimerRef.current = window.setTimeout(() => {
      setEmbed((prev) => (prev === 'present' ? prev : 'missing'))
      embedTimerRef.current = null
    }, EMBED_DETECTION_TIMEOUT_MS)
  }

  if (check.status === 'idle') {
    return (
      <Placeholder icon={<Globe className="size-6 opacity-60" aria-hidden />}>
        Enter a URL to begin
      </Placeholder>
    )
  }

  if (check.status === 'checking') {
    return (
      <Placeholder icon={<Loader2 className="size-6 animate-spin opacity-60" aria-hidden />}>
        Checking page…
      </Placeholder>
    )
  }

  if (check.status === 'blocked') {
    return (
      <Placeholder icon={<ShieldOff className="size-6 opacity-60" aria-hidden />}>
        <span className="font-medium text-foreground">This site doesn’t allow embedding</span>
        <span className="block text-xs">
          The tracking URL still works — sites can refuse to be displayed in a preview without
          affecting the link itself.
        </span>
      </Placeholder>
    )
  }

  return (
    <div className="relative flex h-dvh flex-1 flex-col">
      {check.status === 'unverified' && embed === 'unknown' && (
        <div className="border-b bg-muted/50 px-4 py-2 text-xs text-muted-foreground">
          We couldn’t verify whether this page allows embedding. The preview may fail to
          load even if the tracking URL itself is fine.
        </div>
      )}
      {embed === 'missing' && (
        <div className="border-b bg-muted/50 px-4 py-2 text-xs text-muted-foreground">
          This site doesn’t have the Link Builder helper script installed, so navigating
          within the preview won’t update the page URL.
        </div>
      )}
      <iframe
        key={url}
        ref={iframeRef}
        title="Page preview"
        src={url}
        tabIndex={-1}
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
        referrerPolicy="no-referrer"
        onLoad={handleIframeLoad}
        className="size-full flex-1 border-0 bg-background"
      />
    </div>
  )
}

function Placeholder({
  icon,
  children,
}: {
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-1 items-center justify-center bg-muted/30 text-muted-foreground">
      <div className="flex max-w-sm flex-col items-center gap-2 px-6 text-center text-sm">
        {icon}
        <div>{children}</div>
      </div>
    </div>
  )
}
